"use client";

import { useEffect, useState } from "react";
import { siteBuilderFormForKey } from "./siteBuilderForm";
import { siteBuilderLeadFormFromValue } from "./siteBuilderLeadRuntime";
import type { SiteBuilderLeadForm } from "./siteBuilderTypes";

type SiteBuilderLeadFormLookup = {
    error: boolean;
    form: SiteBuilderLeadForm | null;
    loading: boolean;
};

type RemoteFormState = {
    endpoint: string;
    error: boolean;
    form: SiteBuilderLeadForm | null;
    purpose: string;
};

const pendingForms = new Map<string, Promise<SiteBuilderLeadForm | null>>();
const DEFAULT_FORM_RESOLVE_RETRIES = 5;

export function siteBuilderLeadRetryDelay(attempt: number): number {
    const boundedAttempt = Number.isFinite(attempt)
        ? Math.max(0, Math.min(4, Math.floor(attempt)))
        : 0;

    return Math.min(10_000, 1_000 * (2 ** boundedAttempt));
}

export async function resolveSiteBuilderLeadForm(
    endpoint: string,
    purpose: string,
): Promise<SiteBuilderLeadForm | null> {
    const cacheKey = `${endpoint}:${purpose}`;

    const pending = pendingForms.get(cacheKey);

    if (pending) {
        return pending;
    }

    const request = fetch(`${endpoint}?purpose=${encodeURIComponent(purpose)}`, {
        credentials: "same-origin",
        cache: "no-store",
    }).then(async (response) => {
        if (!response.ok) {
            return null;
        }

        return siteBuilderLeadFormFromValue(await response.json(), purpose);
    }).catch(() => null).finally(() => {
        pendingForms.delete(cacheKey);
    });

    pendingForms.set(cacheKey, request);

    return request;
}

export function observeSiteBuilderLeadForm({
    endpoint,
    onSettled,
    purpose,
    resolveForm = resolveSiteBuilderLeadForm,
    retryDelay = siteBuilderLeadRetryDelay,
    maxRetries = DEFAULT_FORM_RESOLVE_RETRIES,
}: {
    endpoint: string;
    maxRetries?: number;
    onSettled: (form: SiteBuilderLeadForm | null) => void;
    purpose: string;
    resolveForm?: typeof resolveSiteBuilderLeadForm;
    retryDelay?: typeof siteBuilderLeadRetryDelay;
}): () => void {
    let active = true;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const retryLimit = Number.isFinite(maxRetries)
        ? Math.max(0, Math.trunc(maxRetries))
        : DEFAULT_FORM_RESOLVE_RETRIES;

    const load = (): void => {
        void resolveForm(endpoint, purpose).then((form) => {
            if (!active) {
                return;
            }

            onSettled(form);

            if (!active || form !== null || attempt >= retryLimit) {
                return;
            }

            retryTimer = setTimeout(() => {
                retryTimer = null;
                attempt += 1;
                load();
            }, retryDelay(attempt));
        });
    };

    load();

    return () => {
        active = false;

        if (retryTimer !== null) {
            clearTimeout(retryTimer);
        }
    };
}

/**
 * One form resolver for server-provided Web forms and client-resolved Shop
 * forms. A skin receives data and status only; it never owns fetching rules.
 */
export function useSiteBuilderLeadForm({
    enabled = true,
    endpoint = "/api/lead/form",
    formKey,
    forms = [],
}: {
    enabled?: boolean;
    endpoint?: string;
    formKey: string | null | undefined;
    forms?: readonly SiteBuilderLeadForm[];
}): SiteBuilderLeadFormLookup {
    const normalizedPurpose = formKey?.trim() ?? "";
    const providedForm = siteBuilderFormForKey(forms, normalizedPurpose);
    const [remote, setRemote] = useState<RemoteFormState | null>(null);

    useEffect(() => {
        if (!enabled || providedForm || normalizedPurpose === "") {
            return undefined;
        }

        return observeSiteBuilderLeadForm({
            endpoint,
            onSettled: (form) => setRemote({
                endpoint,
                error: form === null,
                form,
                purpose: normalizedPurpose,
            }),
            purpose: normalizedPurpose,
        });
    }, [enabled, endpoint, normalizedPurpose, providedForm]);

    if (providedForm) {
        return { error: false, form: providedForm, loading: false };
    }

    if (!enabled || normalizedPurpose === "") {
        return { error: false, form: null, loading: false };
    }

    const settled = remote?.endpoint === endpoint
        && remote.purpose === normalizedPurpose;

    return {
        error: settled ? remote.error : false,
        form: settled ? remote.form : null,
        loading: !settled,
    };
}
